const ALLOWED_MARKS = ['bold', 'italic', 'underline', 'strike'];

function normalizeText(value) {
  return String(value ?? '')
    .replace(/&nbsp;/g, ' ')
    .replace(/[\u00a0\u2007\u202f]/g, ' ');
}

function marksKey(marks) {
  return (marks || []).join('|');
}

function sanitizeMarks(marks) {
  if (!Array.isArray(marks)) return [];
  const sanitized = [];
  marks.forEach(mark => {
    const value = typeof mark === 'string' ? mark : mark?.type;
    if (ALLOWED_MARKS.includes(value) && !sanitized.includes(value)) {
      sanitized.push(value);
    }
  });
  return sanitized;
}

function textNode(text, marks = []) {
  const cleanText = normalizeText(text);
  if (!cleanText) return null;
  const cleanMarks = sanitizeMarks(marks);
  const node = { type: 'text', text: cleanText };
  if (cleanMarks.length) node.marks = cleanMarks;
  return node;
}

function mergeAdjacentText(nodes) {
  return (nodes || []).reduce((merged, node) => {
    const last = merged[merged.length - 1];
    if (
      last?.type === 'text' &&
      node?.type === 'text' &&
      marksKey(last.marks) === marksKey(node.marks)
    ) {
      last.text += node.text;
      return merged;
    }
    if (node) merged.push(node);
    return merged;
  }, []);
}

function sanitizeInlineContent(content) {
  if (!Array.isArray(content)) return [];
  return mergeAdjacentText(
    content
      .map(node => (node?.type === 'text' ? textNode(node.text, node.marks) : null))
      .filter(Boolean),
  );
}

function paragraphBlock(content = []) {
  return { type: 'paragraph', content };
}

function splitInlineContentIntoParagraphs(content) {
  const paragraphs = [[]];

  sanitizeInlineContent(content).forEach(node => {
    const parts = String(node.text || '')
      .replace(/(?:\r\n?|\n)+/g, '\n')
      .split(/\r\n?|\n/);
    parts.forEach((part, index) => {
      if (index > 0) paragraphs.push([]);
      if (!part) return;

      const splitNode = { type: 'text', text: part };
      if (node.marks?.length) splitNode.marks = node.marks;
      paragraphs[paragraphs.length - 1].push(splitNode);
    });
  });

  const hasContent = paragraphs.some(nodes => nodes.length > 0);
  const visibleParagraphs = hasContent ? paragraphs.filter(nodes => nodes.length > 0) : [[]];
  return visibleParagraphs.map(nodes => paragraphBlock(mergeAdjacentText(nodes)));
}

function blockHasContent(block) {
  if (!block) return false;
  if (block.type === 'paragraph') {
    return (block.content || []).some(node => node?.type === 'text' && node.text);
  }
  if (block.type === 'bulletList' || block.type === 'orderedList') {
    return (block.content || []).some(item => (item.content || []).some(blockHasContent));
  }
  return false;
}

function isEmptyParagraph(block) {
  return block?.type === 'paragraph' && !blockHasContent(block);
}

function sanitizeListItem(node) {
  if (node?.type !== 'listItem') return null;
  const content = sanitizeBlockContent(node.content);
  return {
    type: 'listItem',
    content: content.length ? content : [paragraphBlock()],
  };
}

function sanitizeBlock(node) {
  if (!node || typeof node !== 'object') return [];

  if (node.type === 'paragraph') {
    return splitInlineContentIntoParagraphs(node.content);
  }

  if (node.type === 'bulletList' || node.type === 'orderedList') {
    const content = (Array.isArray(node.content) ? node.content : [])
      .map(sanitizeListItem)
      .filter(Boolean);
    return content.length ? [{ type: node.type, content }] : [];
  }

  return [];
}

function sanitizeBlockContent(content) {
  if (!Array.isArray(content)) return [];
  return compactEmptyParagraphs(content.flatMap(sanitizeBlock));
}

function compactEmptyParagraphs(blocks) {
  return (blocks || []).reduce((compacted, block) => {
    if (isEmptyParagraph(block) && isEmptyParagraph(compacted[compacted.length - 1])) {
      return compacted;
    }
    compacted.push(block);
    return compacted;
  }, []);
}

function plainTextToBody(text) {
  const paragraphs = normalizeText(text).split(/\r?\n/);
  return {
    type: 'doc',
    content: paragraphs.map(paragraph => ({
      type: 'paragraph',
      content: paragraph ? [{ type: 'text', text: paragraph }] : [],
    })),
  };
}

export function sanitizeNoteBody(value) {
  if (typeof value === 'string') return plainTextToBody(value);
  if (!value || typeof value !== 'object' || value.type !== 'doc') {
    return { type: 'doc', content: [paragraphBlock()] };
  }

  const content = sanitizeBlockContent(value.content);
  return { type: 'doc', content: content.length ? content : [paragraphBlock()] };
}

export function normalizeNoteBody(value) {
  return sanitizeNoteBody(value);
}

export function extractNotePlainText(value) {
  const body = sanitizeNoteBody(value);

  const readInline = nodes =>
    (nodes || [])
      .filter(node => node?.type === 'text')
      .map(node => node.text || '')
      .join('');

  const readBlock = block => {
    if (block.type === 'paragraph') return readInline(block.content);
    if (block.type === 'bulletList' || block.type === 'orderedList') {
      return (block.content || [])
        .map(item => (item.content || []).map(readBlock).join('\n'))
        .join('\n');
    }
    return '';
  };

  return (body.content || []).map(readBlock).join('\n');
}

export function isEmptyNoteBody(value) {
  return !extractNotePlainText(value).trim();
}

// Tags that carry no note content and must never contribute text, whether they
// appear at block or inline level.
const NON_CONTENT_TAGS = ['script', 'style', 'img', 'iframe', 'object', 'embed', 'svg', 'math'];

function childNodes(node) {
  return Array.from(node?.childNodes || []);
}

function elementTag(node) {
  return node?.nodeType === Node.ELEMENT_NODE ? node.tagName.toLowerCase() : '';
}

function addMark(marks, mark) {
  return marks.includes(mark) ? marks : [...marks, mark];
}

function readInlineDom(nodes, marks = []) {
  const output = [];

  nodes.forEach(node => {
    if (node.nodeType === Node.TEXT_NODE) {
      const cleanNode = textNode(node.textContent, marks);
      if (cleanNode) output.push(cleanNode);
      return;
    }

    if (node.nodeType !== Node.ELEMENT_NODE) return;

    const tag = elementTag(node);
    if (NON_CONTENT_TAGS.includes(tag)) {
      return;
    }
    if (tag === 'br') {
      output.push(textNode('\n', marks));
      return;
    }

    let nextMarks = marks;
    if (tag === 'strong' || tag === 'b') nextMarks = addMark(nextMarks, 'bold');
    if (tag === 'em' || tag === 'i') nextMarks = addMark(nextMarks, 'italic');
    if (tag === 'u') nextMarks = addMark(nextMarks, 'underline');
    if (tag === 's' || tag === 'strike' || tag === 'del') nextMarks = addMark(nextMarks, 'strike');

    output.push(...readInlineDom(childNodes(node), nextMarks));
  });

  return mergeAdjacentText(output);
}

function isListElement(node) {
  const tag = elementTag(node);
  return tag === 'ul' || tag === 'ol';
}

function isParagraphElement(node) {
  const tag = elementTag(node);
  return tag === 'p' || tag === 'div';
}

// Read a run of sibling DOM nodes into a flat list of block nodes (paragraphs
// and lists). Consecutive inline nodes accumulate into one paragraph; a list or
// a block wrapper (div/p) flushes that paragraph and starts a new block.
//
// Block wrappers recurse rather than being flattened via readInlineDom: some
// browsers — notably WebKit/WKWebView on iOS — emit list markup nested inside a
// <div> after execCommand('insertOrderedList') (e.g. `<div><ol>…</ol></div>`).
// Flattening that wrapper's children as inline dropped the <ol>/<li> tags and
// merged every item into a single run ("test" × 3 → "testtesttest"), so the
// numbered list vanished on save while still showing in the live contenteditable.
function readBlockLevelNodes(nodes) {
  const blocks = [];
  let inlineNodes = [];

  const flushInline = () => {
    if (inlineNodes.length) {
      blocks.push({ type: 'paragraph', content: readInlineDom(inlineNodes) });
      inlineNodes = [];
    }
  };

  nodes.forEach(node => {
    const tag = elementTag(node);
    if (NON_CONTENT_TAGS.includes(tag)) return;

    if (isListElement(node)) {
      flushInline();
      blocks.push(readListDom(node));
      return;
    }

    if (isParagraphElement(node)) {
      flushInline();
      blocks.push(...readBlockLevelNodes(childNodes(node)));
      return;
    }

    inlineNodes.push(node);
  });

  flushInline();
  return blocks;
}

function readListItemDom(li) {
  const content = readBlockLevelNodes(childNodes(li));
  return {
    type: 'listItem',
    content: content.length ? content : [{ type: 'paragraph', content: [] }],
  };
}

function readListDom(node) {
  return {
    type: elementTag(node) === 'ol' ? 'orderedList' : 'bulletList',
    content: childNodes(node)
      .filter(child => elementTag(child) === 'li')
      .map(readListItemDom),
  };
}

export function domToNoteBody(root) {
  return sanitizeNoteBody({ type: 'doc', content: readBlockLevelNodes(childNodes(root)) });
}

function appendMarkedText(doc, parent, node) {
  const text = doc.createTextNode(node.text || '');
  const marks = sanitizeMarks(node.marks);
  if (!marks.length) {
    parent.appendChild(text);
    return;
  }

  const wrappers = {
    bold: 'strong',
    italic: 'em',
    underline: 'u',
    strike: 's',
  };
  let current = text;
  [...marks].reverse().forEach(mark => {
    const wrapper = doc.createElement(wrappers[mark]);
    wrapper.appendChild(current);
    current = wrapper;
  });
  parent.appendChild(current);
}

function appendInlineContent(doc, parent, content) {
  (content || []).forEach(node => {
    if (node.type === 'text') appendMarkedText(doc, parent, node);
  });
}

function blockToElement(doc, block) {
  if (block.type === 'paragraph') {
    const p = doc.createElement('p');
    appendInlineContent(doc, p, block.content);
    if (!p.childNodes.length) p.appendChild(doc.createElement('br'));
    return p;
  }

  if (block.type === 'bulletList' || block.type === 'orderedList') {
    const list = doc.createElement(block.type === 'orderedList' ? 'ol' : 'ul');
    (block.content || []).forEach(item => {
      const li = doc.createElement('li');
      (item.content || []).forEach(child => {
        if (child.type === 'paragraph') appendInlineContent(doc, li, child.content);
        else li.appendChild(blockToElement(doc, child));
      });
      if (!li.childNodes.length) li.appendChild(doc.createElement('br'));
      list.appendChild(li);
    });
    return list;
  }

  return null;
}

export function noteBodyToDomFragment(doc, value) {
  const fragment = doc.createDocumentFragment();
  const body = sanitizeNoteBody(value);
  (body.content || []).forEach(block => {
    const element = blockToElement(doc, block);
    if (element) fragment.appendChild(element);
  });
  return fragment;
}

export { ALLOWED_MARKS };
